// Elakay QR Code Designer -- rendering, export and verification.
//
// Three modes:
//  - classic:  dark modules on a light background (square / rounded / dot);
//  - logo:     classic + a logo in the middle (error correction H);
//  - artistic: a real photograph runs THROUGH the code -- every module shows
//              the photo, tinted toward its dark/light value, with a solid
//              core at its center (what scanners sample). Finder, timing and
//              alignment patterns stay solid, the quiet zone stays light.
// Every render can be decoded back (jsQR) to check it holds exactly the link.
import QRCode from "qrcode";
import jsQR from "jsqr";

export const QR_MODES = ["classic", "logo", "artistic"];
export const EXPORT_SIZES = [512, 640, 1024, 1080, 2048];
export const EXPORT_FORMATS = ["png", "jpeg", "svg", "pdf"];
const QUIET = 4; // modules of light margin around the code (QR standard)

export const DEFAULT_SETTINGS = Object.freeze({
  ecLevel: "auto",
  canvasColor: "#ffffff",
  canvasTransparent: false,
  backgroundScale: 1,
  backgroundX: 0,
  backgroundY: 0,
  backgroundOpacity: 1,
  qrScale: 1,
  qrX: 0,
  qrY: 0,
  darkColor: "#111111",
  lightColor: "#ffffff",
  lightTransparent: false,
  moduleStyle: "square",
  logoSize: 20,
  logoX: 0,
  logoY: 0,
  logoPlate: true,
  logoPlateColor: "#ffffff",
  logoPlateTransparent: false,
  photoScale: 1,
  photoX: 0,
  photoY: 0,
  photoOpacity: 1,
  brightness: 1,
  contrast: 0.6,
  coreSize: 0.45,
  text: "",
  textColor: "#111111",
  textSize: 8,
  textPosition: "bottom",
  textBold: true,
});

const clampNumber = (value, min, max, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.min(max, Math.max(min, number)) : fallback;
};
const isColor = (value) => /^#[0-9a-f]{6}$/i.test(String(value || ""));

/** Settings with every value present, typed and inside its range. */
export const normalizeSettings = (input = {}) => {
  const s = { ...DEFAULT_SETTINGS, ...(input && typeof input === "object" ? input : {}) };
  return {
    ecLevel: ["auto", "L", "M", "Q", "H"].includes(s.ecLevel) ? s.ecLevel : "auto",
    canvasColor: isColor(s.canvasColor) ? s.canvasColor : DEFAULT_SETTINGS.canvasColor,
    canvasTransparent: !!s.canvasTransparent,
    backgroundScale: clampNumber(s.backgroundScale, 0.5, 3, 1),
    backgroundX: clampNumber(s.backgroundX, -50, 50, 0),
    backgroundY: clampNumber(s.backgroundY, -50, 50, 0),
    backgroundOpacity: clampNumber(s.backgroundOpacity, 0.05, 1, 1),
    qrScale: clampNumber(s.qrScale, 0.45, 1, 1),
    qrX: clampNumber(s.qrX, -50, 50, 0),
    qrY: clampNumber(s.qrY, -50, 50, 0),
    darkColor: isColor(s.darkColor) ? s.darkColor : DEFAULT_SETTINGS.darkColor,
    lightColor: isColor(s.lightColor) ? s.lightColor : DEFAULT_SETTINGS.lightColor,
    lightTransparent: !!s.lightTransparent,
    moduleStyle: ["square", "rounded", "dot"].includes(s.moduleStyle) ? s.moduleStyle : "square",
    logoSize: clampNumber(s.logoSize, 10, 28, 20),
    logoX: clampNumber(s.logoX, -50, 50, 0),
    logoY: clampNumber(s.logoY, -50, 50, 0),
    logoPlate: s.logoPlate !== false,
    logoPlateColor: isColor(s.logoPlateColor) ? s.logoPlateColor : DEFAULT_SETTINGS.logoPlateColor,
    logoPlateTransparent: !!s.logoPlateTransparent,
    photoScale: clampNumber(s.photoScale, 0.5, 3, 1),
    photoX: clampNumber(s.photoX, -50, 50, 0),
    photoY: clampNumber(s.photoY, -50, 50, 0),
    photoOpacity: clampNumber(s.photoOpacity, 0.1, 1, 1),
    brightness: clampNumber(s.brightness, 0.5, 1.5, 1),
    contrast: clampNumber(s.contrast, 0.2, 0.95, 0.6),
    coreSize: clampNumber(s.coreSize, 0.25, 0.75, 0.45),
    text: String(s.text || "").slice(0, 80),
    textColor: isColor(s.textColor) ? s.textColor : DEFAULT_SETTINGS.textColor,
    textSize: clampNumber(s.textSize, 4, 14, 8),
    textPosition: s.textPosition === "top" ? "top" : "bottom",
    textBold: s.textBold !== false,
  };
};

/** Error correction: H when an image covers or crosses the modules. */
export const resolveEcLevel = (mode, settings) => {
  if (settings.ecLevel && settings.ecLevel !== "auto") return settings.ecLevel;
  return mode === "classic" ? "M" : "H";
};

export const buildMatrix = (link, ecLevel = "M") => {
  const qr = QRCode.create(String(link), { errorCorrectionLevel: ecLevel });
  const { modules } = qr;
  return {
    size: modules.size,
    version: qr.version,
    isDark: (row, col) => !!modules.get(row, col),
    // Finder / separator / timing / alignment / format / version modules.
    isReserved: (row, col) => !!modules.isReserved(row, col),
  };
};

/** Geometry of a render of `px` × `px` pixels. */
export const computeLayout = (px, matrixSize, settings) => {
  const band = settings.text ? Math.round(px * (settings.textSize / 100) * 1.7) : 0;
  const qrBox = px - band;
  const m = Math.max(1, Math.floor((qrBox * settings.qrScale) / (matrixSize + QUIET * 2)));
  const qrDraw = m * (matrixSize + QUIET * 2);
  const availableX = Math.max(0, px - qrDraw);
  const availableY = Math.max(0, qrBox - qrDraw);
  const qrX = Math.round((availableX / 2) * (1 + settings.qrX / 50));
  const qrY = (settings.textPosition === "top" ? band : 0) + Math.round((availableY / 2) * (1 + settings.qrY / 50));
  const bandY = settings.textPosition === "top" ? 0 : px - band;
  return { px, band, bandY, m, qrDraw, qrX, qrY, originX: qrX + QUIET * m, originY: qrY + QUIET * m, dataSize: matrixSize * m };
};

const hexToRgba = (hex, alpha) => {
  const value = parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255},${(value >> 8) & 255},${value & 255},${alpha})`;
};

const drawModuleShape = (ctx, x, y, size, style) => {
  if (style === "dot") {
    ctx.beginPath();
    ctx.arc(x + size / 2, y + size / 2, size * 0.46, 0, Math.PI * 2);
    ctx.fill();
  } else if (style === "rounded" && ctx.roundRect) {
    ctx.beginPath();
    ctx.roundRect(x + size * 0.04, y + size * 0.04, size * 0.92, size * 0.92, size * 0.3);
    ctx.fill();
  } else {
    ctx.fillRect(x, y, size, size);
  }
};

// Cover-fit of the photo inside the data area, with zoom and offsets (%).
const photoRect = (img, layout, settings) => {
  const area = layout.dataSize;
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const scale = Math.max(area / iw, area / ih) * settings.photoScale;
  const w = iw * scale;
  const h = ih * scale;
  return {
    x: layout.originX + (area - w) / 2 + (settings.photoX / 100) * area,
    y: layout.originY + (area - h) / 2 + (settings.photoY / 100) * area,
    w,
    h,
  };
};

const coverRect = (img, px, scaleSetting, xSetting, ySetting) => {
  const iw = img.naturalWidth || img.width;
  const ih = img.naturalHeight || img.height;
  const scale = Math.max(px / iw, px / ih) * scaleSetting;
  const w = iw * scale;
  const h = ih * scale;
  return { x: (px - w) / 2 + (xSetting / 100) * px, y: (px - h) / 2 + (ySetting / 100) * px, w, h };
};

const logoRect = (layout, settings) => {
  const size = Math.round(layout.dataSize * (settings.logoSize / 100));
  return {
    x: layout.originX + (layout.dataSize - size) / 2 + (settings.logoX / 100) * layout.dataSize,
    y: layout.originY + (layout.dataSize - size) / 2 + (settings.logoY / 100) * layout.dataSize,
    size,
    plate: Math.round(size * 0.12),
  };
};

/** A QR cut-out is needed only when an actual opaque plate is drawn. */
export const hasOpaqueLogoPlate = (settings) => settings.logoPlate && !settings.logoPlateTransparent;

const fitFont = (ctx, text, weight, maxWidth, startPx) => {
  let size = startPx;
  ctx.font = `${weight} ${size}px system-ui, -apple-system, "Segoe UI", Arial, sans-serif`;
  while (size > 6 && ctx.measureText(text).width > maxWidth) {
    size -= 1;
    ctx.font = `${weight} ${size}px system-ui, -apple-system, "Segoe UI", Arial, sans-serif`;
  }
  return size;
};

/**
 * Draws a design on `canvas` at `px` × `px`.
 * design: { link, mode, settings, backgroundImg, photoImg, logoImg }
 * options.layer: "all" (default) | "photo" (artistic photo + tints only, the
 * raster part of an SVG export).
 * Returns the layout and the matrix.
 */
export const renderDesign = (canvas, design, px, options = {}) => {
  const settings = normalizeSettings(design.settings);
  const mode = QR_MODES.includes(design.mode) ? design.mode : "classic";
  const matrix = buildMatrix(design.link, resolveEcLevel(mode, settings));
  const layout = computeLayout(px, matrix.size, settings);
  canvas.width = px;
  canvas.height = px;
  const ctx = canvas.getContext("2d");
  ctx.clearRect(0, 0, px, px);
  const onlyPhoto = options.layer === "photo";
  const { m } = layout;

  // Layer 1: independent design background (colour / transparency / image).
  if (!settings.canvasTransparent) {
    ctx.fillStyle = settings.canvasColor;
    ctx.fillRect(0, 0, px, px);
  }
  if (design.backgroundImg) {
    const rect = coverRect(design.backgroundImg, px, settings.backgroundScale, settings.backgroundX, settings.backgroundY);
    ctx.save();
    ctx.globalAlpha = settings.backgroundOpacity;
    ctx.drawImage(design.backgroundImg, rect.x, rect.y, rect.w, rect.h);
    ctx.restore();
  }

  // Layer 2: the QR's own background, including its mandatory quiet zone.
  if (!settings.lightTransparent) {
    ctx.fillStyle = settings.lightColor;
    ctx.fillRect(layout.qrX, layout.qrY, layout.qrDraw, layout.qrDraw);
  }

  const logo = mode === "logo" && design.logoImg ? logoRect(layout, settings) : null;
  const underLogo = (row, col) => {
    // A transparent PNG/WebP must be composited directly over the QR. Cutting
    // the modules for a disabled/transparent plate creates the coloured
    // rectangle that users perceive as a logo-container background.
    if (!logo || !hasOpaqueLogoPlate(settings)) return false;
    const x = layout.originX + col * m;
    const y = layout.originY + row * m;
    const pad = logo.plate;
    return x + m > logo.x - pad && x < logo.x + logo.size + pad && y + m > logo.y - pad && y < logo.y + logo.size + pad;
  };

  if (mode === "artistic" && design.photoImg) {
    const rect = photoRect(design.photoImg, layout, settings);
    ctx.save();
    ctx.beginPath();
    ctx.rect(layout.originX, layout.originY, layout.dataSize, layout.dataSize);
    ctx.clip();
    ctx.globalAlpha = settings.photoOpacity;
    ctx.filter = `brightness(${settings.brightness})`;
    ctx.drawImage(design.photoImg, rect.x, rect.y, rect.w, rect.h);
    ctx.filter = "none";
    ctx.globalAlpha = 1;
    // Each data module is pulled toward its value, the photo stays visible.
    const tint = settings.contrast * 0.62;
    for (let row = 0; row < matrix.size; row += 1) {
      for (let col = 0; col < matrix.size; col += 1) {
        if (matrix.isReserved(row, col)) continue;
        ctx.fillStyle = hexToRgba(matrix.isDark(row, col) ? settings.darkColor : settings.lightColor, tint);
        ctx.fillRect(layout.originX + col * m, layout.originY + row * m, m, m);
      }
    }
    ctx.restore();
  }

  if (!onlyPhoto) {
    const core = settings.coreSize * m;
    for (let row = 0; row < matrix.size; row += 1) {
      for (let col = 0; col < matrix.size; col += 1) {
        const x = layout.originX + col * m;
        const y = layout.originY + row * m;
        const dark = matrix.isDark(row, col);
        const reserved = matrix.isReserved(row, col);
        if (mode === "artistic" && design.photoImg) {
          // Patterns solid (they locate the code), data modules = core only.
          ctx.fillStyle = dark ? settings.darkColor : settings.lightColor;
          if (reserved) ctx.fillRect(x, y, m, m);
          else ctx.fillRect(x + (m - core) / 2, y + (m - core) / 2, core, core);
        } else if (dark && !underLogo(row, col)) {
          ctx.fillStyle = settings.darkColor;
          if (reserved) ctx.fillRect(x, y, m, m);
          else drawModuleShape(ctx, x, y, m, settings.moduleStyle);
        }
      }
    }

    if (logo) {
      if (hasOpaqueLogoPlate(settings)) {
        ctx.fillStyle = settings.logoPlateColor;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(logo.x - logo.plate, logo.y - logo.plate, logo.size + logo.plate * 2, logo.size + logo.plate * 2, logo.plate);
        else ctx.rect(logo.x - logo.plate, logo.y - logo.plate, logo.size + logo.plate * 2, logo.size + logo.plate * 2);
        ctx.fill();
      }
      const img = design.logoImg;
      const iw = img.naturalWidth || img.width;
      const ih = img.naturalHeight || img.height;
      const scale = Math.min(logo.size / iw, logo.size / ih);
      ctx.drawImage(img, logo.x + (logo.size - iw * scale) / 2, logo.y + (logo.size - ih * scale) / 2, iw * scale, ih * scale);
    }

    if (settings.text) {
      const weight = settings.textBold ? 700 : 500;
      const fontPx = fitFont(ctx, settings.text, weight, px * 0.92, Math.round(px * (settings.textSize / 100)));
      ctx.fillStyle = settings.textColor;
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(settings.text, px / 2, layout.bandY + layout.band / 2);
      layout.fontPx = fontPx;
    }
  }
  return { layout, matrix, settings, mode };
};

// ---------------------------------------------------------------------------
// Verification: decode the image and compare with the expected link.
// ---------------------------------------------------------------------------

/** Decodes a canvas (over white, so transparent codes are read as printed). */
export const decodeCanvas = (canvas) => {
  const work = document.createElement("canvas");
  const maxSide = 1024;
  const scale = Math.min(1, maxSide / Math.max(canvas.width, canvas.height));
  work.width = Math.round(canvas.width * scale);
  work.height = Math.round(canvas.height * scale);
  const ctx = work.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, work.width, work.height);
  ctx.drawImage(canvas, 0, 0, work.width, work.height);
  let data;
  try {
    data = ctx.getImageData(0, 0, work.width, work.height);
  } catch (error) {
    return { ok: false, decoded: null, error: "TAINTED" };
  }
  const result = jsQR(data.data, work.width, work.height, { inversionAttempts: "attemptBoth" });
  return { decoded: result ? result.data : null };
};

export const verifyCanvas = (canvas, expected) => {
  const { decoded, error } = decodeCanvas(canvas);
  return { ok: !error && decoded === expected, decoded: decoded ?? null, error: error || (decoded === null ? "UNREADABLE" : decoded === expected ? null : "MISMATCH") };
};

// ---------------------------------------------------------------------------
// Export
// ---------------------------------------------------------------------------

const escapeXml = (value) => String(value).replace(/[<>&"']/g, (c) => ({ "<": "&lt;", ">": "&gt;", "&": "&amp;", '"': "&quot;", "'": "&apos;" }[c]));

const imageDataUrl = (img) => {
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth || img.width;
  canvas.height = img.naturalHeight || img.height;
  canvas.getContext("2d").drawImage(img, 0, 0);
  return canvas.toDataURL("image/png");
};

/** SVG: vector modules, text and logo frame; the artistic photo layer is embedded. */
export const renderSvg = (design, px) => {
  const canvas = document.createElement("canvas");
  const { layout, matrix, settings, mode } = renderDesign(canvas, design, px, { layer: "photo" });
  const { m } = layout;
  const parts = [`<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${px}" height="${px}" viewBox="0 0 ${px} ${px}">`];
  const artistic = mode === "artistic" && design.photoImg;
  if (artistic || design.backgroundImg) {
    parts.push(`<image x="0" y="0" width="${px}" height="${px}" href="${canvas.toDataURL("image/png")}"/>`);
  } else {
    if (!settings.canvasTransparent) parts.push(`<rect width="${px}" height="${px}" fill="${settings.canvasColor}"/>`);
    if (!settings.lightTransparent) parts.push(`<rect x="${layout.qrX}" y="${layout.qrY}" width="${layout.qrDraw}" height="${layout.qrDraw}" fill="${settings.lightColor}"/>`);
  }
  const logo = mode === "logo" && design.logoImg ? logoRect(layout, settings) : null;
  const core = settings.coreSize * m;
  const dark = [];
  const light = [];
  for (let row = 0; row < matrix.size; row += 1) {
    for (let col = 0; col < matrix.size; col += 1) {
      const x = layout.originX + col * m;
      const y = layout.originY + row * m;
      const isDark = matrix.isDark(row, col);
      const reserved = matrix.isReserved(row, col);
      if (artistic) {
        const target = isDark ? dark : light;
        if (reserved) target.push(`M${x} ${y}h${m}v${m}h-${m}z`);
        else target.push(`M${x + (m - core) / 2} ${y + (m - core) / 2}h${core}v${core}h-${core}z`);
      } else if (isDark) {
        if (logo && hasOpaqueLogoPlate(settings)) {
          const pad = logo.plate;
          if (x + m > logo.x - pad && x < logo.x + logo.size + pad && y + m > logo.y - pad && y < logo.y + logo.size + pad) continue;
        }
        if (!reserved && settings.moduleStyle === "dot") dark.push(`M${x + m / 2 - m * 0.46} ${y + m / 2}a${m * 0.46} ${m * 0.46} 0 1 0 ${m * 0.92} 0a${m * 0.46} ${m * 0.46} 0 1 0 -${m * 0.92} 0z`);
        else dark.push(`M${x} ${y}h${m}v${m}h-${m}z`);
      }
    }
  }
  if (light.length) parts.push(`<path fill="${settings.lightColor}" d="${light.join("")}"/>`);
  if (dark.length) parts.push(`<path fill="${settings.darkColor}" d="${dark.join("")}"/>`);
  if (logo) {
    if (hasOpaqueLogoPlate(settings)) parts.push(`<rect x="${logo.x - logo.plate}" y="${logo.y - logo.plate}" width="${logo.size + logo.plate * 2}" height="${logo.size + logo.plate * 2}" rx="${logo.plate}" fill="${settings.logoPlateColor}"/>`);
    parts.push(`<image x="${logo.x}" y="${logo.y}" width="${logo.size}" height="${logo.size}" preserveAspectRatio="xMidYMid meet" href="${imageDataUrl(design.logoImg)}"/>`);
  }
  if (settings.text) {
    // Same size as the bitmap render when it fits; shrunk to ~92 % width otherwise.
    const fontPx = Math.min(Math.round(px * (settings.textSize / 100)), Math.floor((px * 0.92) / (settings.text.length * 0.6)));
    parts.push(`<text x="${px / 2}" y="${layout.bandY + layout.band / 2}" text-anchor="middle" dominant-baseline="central" font-family="system-ui, -apple-system, Segoe UI, Arial, sans-serif" font-weight="${settings.textBold ? 700 : 500}" font-size="${Math.max(6, fontPx)}" fill="${settings.textColor}">${escapeXml(settings.text)}</text>`);
  }
  parts.push("</svg>");
  return parts.join("");
};

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("IMAGE_LOAD_FAILED"));
    img.src = src;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve) => canvas.toBlob((blob) => resolve(blob), type, quality));

const verifyBlobImage = async (blob, size, link) => {
  const url = URL.createObjectURL(blob);
  try {
    const img = await loadImage(url);
    const check = document.createElement("canvas");
    check.width = size;
    check.height = size;
    check.getContext("2d").drawImage(img, 0, 0, size, size);
    return verifyCanvas(check, link);
  } finally {
    URL.revokeObjectURL(url);
  }
};

/**
 * Builds the export file and checks it: the exported image is decoded again
 * (PNG / JPEG / SVG rendered back to pixels; PDF: its page image + header).
 * Returns { blob, filename, verification }.
 */
export const exportDesign = async (design, { format = "png", size = 1024, name = "qr-code" } = {}) => {
  if (!EXPORT_FORMATS.includes(format) || !EXPORT_SIZES.includes(size)) throw new Error("UNSUPPORTED_EXPORT");
  const base = String(name || "qr-code").replace(/[^a-z0-9_-]+/gi, "-").replace(/^-|-$/g, "").slice(0, 60) || "qr-code";
  const filename = `${base}-${size}.${format === "jpeg" ? "jpg" : format}`;

  if (format === "svg") {
    const svg = renderSvg(design, size);
    const blob = new Blob([svg], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    try {
      const img = await loadImage(url);
      const check = document.createElement("canvas");
      check.width = size;
      check.height = size;
      check.getContext("2d").drawImage(img, 0, 0, size, size);
      return { blob, filename, verification: verifyCanvas(check, design.link) };
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  const canvas = document.createElement("canvas");
  renderDesign(canvas, design, size);
  if (format === "jpeg") {
    // JPEG has no transparency: flatten on white.
    const flat = document.createElement("canvas");
    flat.width = size;
    flat.height = size;
    const ctx = flat.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, size, size);
    ctx.drawImage(canvas, 0, 0);
    const blob = await canvasToBlob(flat, "image/jpeg", 0.95);
    return { blob, filename, verification: await verifyBlobImage(blob, size, design.link) };
  }

  const pngBlob = await canvasToBlob(canvas, "image/png");
  const verification = await verifyBlobImage(pngBlob, size, design.link);
  if (format === "png") return { blob: pngBlob, filename, verification };

  const { jsPDF } = await import("jspdf");
  const pdf = new jsPDF({ unit: "px", format: [size, size], orientation: "portrait", hotfixes: ["px_scaling"] });
  pdf.addImage(canvas.toDataURL("image/png"), "PNG", 0, 0, size, size, undefined, "FAST");
  const blob = pdf.output("blob");
  const head = await blob.slice(0, 5).text();
  return { blob, filename, verification: { ...verification, ok: verification.ok && head === "%PDF-" } };
};
